import { API, graphqlOperation } from 'aws-amplify';
import { Button, DatePicker, Input, Modal, Select, Space, Spin, Table, Tag, Popconfirm, Tooltip } from 'antd';
import { DeleteOutlined, DownloadOutlined, EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import { withRouter } from 'react-router-dom';
import { MainLayout } from '../../components/Layout';
import AssistantConversationReviewModal, {
  REVIEW_STATUS,
  REVIEW_STATUS_COLORS,
  buildReviewUpdateData,
  getEffectiveReviewStatus,
  getFailureReasonLabel,
  getReviewStatusLabel,
} from '../../components/AssistantConversationReviewModal/AssistantConversationReviewModal';
import { Admin, NoSql } from '../../queries';
import {
  deleteAssistantConversation,
  getAssistantConversation,
  getAssistantConversationUserGroups,
  getCountOfAssistantConversations,
  getManyAssistantConversations,
  updateAssistantConversation,
} from '../../utils/NoSqlUtils';
import arabicNormalize from '../../utils/arabicNormalize';
import ConversationReplay from './ConversationReplay';
import { getListState, setListState } from './listStateStore';

const { RangePicker } = DatePicker;
const { Option } = Select;

const DEFAULT_PAGE_SIZE = 10;

const FIND_MANY_QUERY_TEXT = (() => {
  try {
    return NoSql.AssistantConversation.findManyAssistantConversations().loc.source.body.trim();
  } catch (error) {
    return 'Unable to read the GraphQL query document.';
  }
})();

const codeBlockStyle = {
  background: '#f5f5f5',
  borderRadius: 8,
  padding: 12,
  maxHeight: 280,
  overflow: 'auto',
  fontFamily: 'monospace',
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
};

const initialFilters = {
  userIds: [],
  searchText: '',
  flagged: undefined,
  reviewStatus: undefined,
  dateRange: null,
};

const failureExportHeaders = [
  { label: 'Conversation Mongo ID', key: 'id' },
  { label: 'Conversation ID', key: 'conversationId' },
  { label: 'User ID', key: 'userId' },
  { label: 'Username', key: 'username' },
  { label: 'Failure Reason', key: 'failureReason' },
  { label: 'Failure Note', key: 'failureNote' },
  { label: 'Reviewed By ID', key: 'reviewedById' },
  { label: 'Reviewed By Name', key: 'reviewedByName' },
  { label: 'Reviewed At', key: 'reviewedAt' },
  { label: 'Created At', key: 'createdAt' },
  { label: 'Updated At', key: 'updatedAt' },
];

const textFilter = (value) => ({ contains: value, mode: 'insensitive' });
const equalsFilter = (value) => ({ equals: value });

const getTrimmedValue = (value) => {
  if (value === undefined || value === null) return '';
  return value.toString().trim();
};

const buildAssistantConversationsWhere = (filters) => {
  const where = {};
  const AND = [];

  const userIds = Array.isArray(filters.userIds)
    ? filters.userIds.filter(Boolean)
    : [];
  if (userIds.length) {
    AND.push({ userId: { in: userIds } });
  }

  const searchText = getTrimmedValue(filters.searchText);
  if (searchText) {
    AND.push({ searchText: textFilter(searchText) });
  }

  if (filters.flagged === 'flagged') {
    AND.push({ flagged: equalsFilter(true) });
  }

  if (filters.flagged === 'unflagged') {
    AND.push({ flagged: equalsFilter(false) });
  }

  if (filters.reviewStatus === 'unreviewed') {
    AND.push({
      OR: [
        { reviewStatus: equalsFilter(REVIEW_STATUS.PENDING) },
        { reviewStatus: equalsFilter(null) },
      ],
    });
  }

  if (filters.reviewStatus === REVIEW_STATUS.SUCCESS) {
    AND.push({ reviewStatus: equalsFilter(REVIEW_STATUS.SUCCESS) });
  }

  if (filters.reviewStatus === REVIEW_STATUS.FAILURE) {
    AND.push({ reviewStatus: equalsFilter(REVIEW_STATUS.FAILURE) });
  }

  if (filters.dateRange && filters.dateRange.length) {
    const [from, to] = filters.dateRange;
    const createdAt = {};
    if (from) createdAt.gte = from.clone().startOf('day').toISOString();
    if (to) createdAt.lte = to.clone().endOf('day').toISOString();
    if (Object.keys(createdAt).length) {
      AND.push({ createdAt });
    }
  }

  if (AND.length) {
    where.AND = AND;
  }

  return where;
};

const getCountFromResult = (result) =>
  result &&
  result.data &&
  result.data.aggregateAssistantConversation &&
  result.data.aggregateAssistantConversation._count
    ? result.data.aggregateAssistantConversation._count.id || 0
    : 0;

const getOptionText = (option, field) => {
  if (!option) return '';
  if (option[field]) return option[field];
  if (option.props && option.props[field]) return option.props[field];
  return '';
};

const userFilterOption = (input, option) => {
  const normalizedInput = arabicNormalize(input);
  const label = getOptionText(option, 'label');
  const value = getOptionText(option, 'value');
  return (
    arabicNormalize(label).indexOf(normalizedInput) !== -1 ||
    arabicNormalize(value).indexOf(normalizedInput) !== -1
  );
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');

function AssistantConversations({ history, allCookies }) {
  const cachedListState = getListState();
  const [filters, setFilters] = useState(cachedListState.filters || initialFilters);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(cachedListState.current || 1);
  const [pageSize, setPageSize] = useState(cachedListState.pageSize || DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [userNameById, setUserNameById] = useState({});
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [detailById, setDetailById] = useState({});
  const [detailLoadingById, setDetailLoadingById] = useState({});
  const [queryModalVisible, setQueryModalVisible] = useState(false);
  const [lastQueryVariables, setLastQueryVariables] = useState(null);

  const fetchConversations = async ({
    page = current,
    nextPageSize = pageSize,
    nextFilters = filters,
  } = {}) => {
    const where = buildAssistantConversationsWhere(nextFilters);
    const variables = {
      where,
      take: nextPageSize,
      skip: nextPageSize * (page - 1),
      orderBy: { createdAt: 'desc' },
    };
    setLastQueryVariables(variables);
    setListState({ filters: nextFilters, current: page, pageSize: nextPageSize });
    setLoading(true);

    try {
      const [listResult, countResult] = await Promise.all([
        getManyAssistantConversations(variables),
        getCountOfAssistantConversations({ where }),
      ]);

      const data =
        (listResult &&
          listResult.data &&
          listResult.data.assistantConversations) ||
        [];

      setConversations(
        data.map((conversation, index) => ({
          ...conversation,
          key: conversation.id || index,
        }))
      );
      setTotal(getCountFromResult(countResult));
      setCurrent(page);
      setPageSize(nextPageSize);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationUsers = async () => {
    setUsersLoading(true);
    try {
      const groupResult = await getAssistantConversationUserGroups();
      const groups =
        (groupResult &&
          groupResult.data &&
          groupResult.data.groupByAssistantConversation) ||
        [];
      const userIds = groups
        .map((group) => group && group.userId)
        .filter(Boolean);
      const uniqueUserIds = Array.from(new Set(userIds));
      const nextNameById = uniqueUserIds.reduce((map, userId) => {
        map[userId] = userId;
        return map;
      }, {});

      if (uniqueUserIds.length) {
        const namesResult = await API.graphql(
          graphqlOperation(Admin.User.GET_USERS_NAMES, {
            where: { id: { in: uniqueUserIds } },
          })
        );
        const users =
          (namesResult && namesResult.data && namesResult.data.users) || [];
        users.forEach((user) => {
          if (user && user.id) {
            nextNameById[user.id] = user.name || user.id;
          }
        });
      }

      const options = uniqueUserIds
        .map((userId) => ({
          value: userId,
          label: nextNameById[userId] || userId,
        }))
        .sort((a, b) => arabicNormalize(a.label).localeCompare(arabicNormalize(b.label)));

      setUserNameById(nextNameById);
      setUserOptions(options);
    } catch (error) {
      console.log('AssistantConversations loadConversationUsers error:', error);
      setUserNameById({});
      setUserOptions([]);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    const restoreScrollY = cachedListState.scrollY || 0;
    fetchConversations().then(() => {
      if (restoreScrollY) {
        window.requestAnimationFrame(() => window.scrollTo(0, restoreScrollY));
      }
    });
    loadConversationUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (field, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [field]: value }));
  };

  const applyFilters = () => {
    fetchConversations({ page: 1, nextFilters: filters });
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    fetchConversations({ page: 1, nextFilters: initialFilters });
  };

  const handleTableChange = (pagination) => {
    fetchConversations({
      page: pagination.current,
      nextPageSize: pagination.pageSize,
    });
  };

  const handleExpand = async (expanded, record) => {
    setExpandedRowKeys(expanded ? [record.key] : []);
    if (expanded && !detailById[record.id] && !detailLoadingById[record.id]) {
      setDetailLoadingById((prev) => ({ ...prev, [record.id]: true }));
      try {
        const result = await getAssistantConversation({ id: record.id });
        const detail =
          (result && result.data && result.data.assistantConversation) || null;
        setDetailById((prev) => ({ ...prev, [record.id]: detail }));
      } finally {
        setDetailLoadingById((prev) => ({ ...prev, [record.id]: false }));
      }
    }
  };

  const renderExpandedConversation = (record) => {
    if (detailLoadingById[record.id]) {
      return <Spin>Loading conversation…</Spin>;
    }
    const detail = detailById[record.id];
    if (!detail) {
      return <div style={{ color: '#999' }}>Conversation could not be loaded.</div>;
    }
    return <ConversationReplay conversation={detail} maxHeight="50vh" />;
  };

  const openFullView = (record) => {
    setListState({ scrollY: window.scrollY });
    history.push(`/assistant-conversation/details/${record.id}`);
  };

  const handleFlagToggle = async (record) => {
    const nextFlagged = !record.flagged;
    await updateAssistantConversation({
      id: record.id,
      data: {
        flagged: { set: nextFlagged },
        flaggedAt: { set: nextFlagged ? new Date().toISOString() : null },
      },
    });
    fetchConversations();
  };

  const openReviewModal = (record) => {
    setReviewTarget(record);
  };

  const closeReviewModal = () => {
    setReviewTarget(null);
  };

  const handleReviewSave = async (values) => {
    if (!reviewTarget) return;

    setReviewSaving(true);
    try {
      await updateAssistantConversation({
        id: reviewTarget.id,
        data: buildReviewUpdateData(values, allCookies),
      });
      closeReviewModal();
      fetchConversations();
    } finally {
      setReviewSaving(false);
    }
  };

  const handleDelete = async (record) => {
    await deleteAssistantConversation({ id: record.id });
    fetchConversations({ page: 1 });
  };

  const renderUsername = (userId) => {
    if (!userId) return <Tag>Anonymous</Tag>;
    if (usersLoading && !userNameById[userId]) return <Tag>Loading...</Tag>;
    return userNameById[userId] || userId;
  };

  const renderReviewStatus = (record) => {
    const status = getEffectiveReviewStatus(record);
    const tooltip = (
      <div>
        <div>{getReviewStatusLabel(status)}</div>
        {record.reviewedByName && <div>Reviewed by {record.reviewedByName}</div>}
        {record.reviewedAt && <div>{formatDate(record.reviewedAt)}</div>}
        {status === REVIEW_STATUS.FAILURE && record.failureReason && (
          <div>Reason: {getFailureReasonLabel(record.failureReason)}</div>
        )}
        {status === REVIEW_STATUS.FAILURE && record.failureNote && (
          <div>Note: {record.failureNote}</div>
        )}
      </div>
    );

    return (
      <Tooltip title={tooltip}>
        <Tag color={REVIEW_STATUS_COLORS[status]}>{getReviewStatusLabel(status)}</Tag>
      </Tooltip>
    );
  };

  const failureExportData = conversations
    .filter((conversation) => getEffectiveReviewStatus(conversation) === REVIEW_STATUS.FAILURE)
    .map((conversation) => ({
      id: conversation.id || '',
      conversationId: conversation.conversationId || '',
      userId: conversation.userId || '',
      username: conversation.userId ? userNameById[conversation.userId] || conversation.userId : 'Anonymous',
      failureReason: conversation.failureReason || '',
      failureNote: conversation.failureNote || '',
      reviewedById: conversation.reviewedById || '',
      reviewedByName: conversation.reviewedByName || '',
      reviewedAt: conversation.reviewedAt || '',
      createdAt: conversation.createdAt || '',
      updatedAt: conversation.updatedAt || '',
    }));

  const columns = [
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: formatDate,
    },
    {
      title: 'Username',
      dataIndex: 'userId',
      key: 'username',
      render: renderUsername,
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (value) => value || <Tag>Anonymous</Tag>,
    },
    {
      title: 'Conversation ID',
      dataIndex: 'conversationId',
      key: 'conversationId',
      render: (value) => <span style={{ fontFamily: 'monospace' }}>{value}</span>,
    },
    {
      title: 'Preview',
      dataIndex: 'lastMessagePreview',
      key: 'lastMessagePreview',
      render: (value) => value || '-',
    },
    {
      title: 'Turns',
      dataIndex: 'turnCount',
      key: 'turnCount',
      render: (value) => value || 0,
    },
    {
      title: 'Locale',
      dataIndex: 'locale',
      key: 'locale',
      render: (value) => value || '-',
    },
    {
      title: 'Flag',
      dataIndex: 'flagged',
      key: 'flagged',
      render: (flagged) =>
        flagged ? <Tag color="red">Flagged</Tag> : <Tag>Unflagged</Tag>,
    },
    {
      title: 'Review',
      key: 'reviewStatus',
      render: (_, record) => renderReviewStatus(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => openFullView(record)}>
            View
          </Button>
          <Button onClick={() => openReviewModal(record)}>Review</Button>
          <Button onClick={() => handleFlagToggle(record)}>
            {record.flagged ? 'Unflag' : 'Flag'}
          </Button>
          <Popconfirm
            title="Delete this assistant conversation?"
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout title="Assistant Conversations">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Select
            allowClear
            showSearch
            mode="multiple"
            placeholder="Users"
            style={{ width: 320 }}
            value={filters.userIds}
            loading={usersLoading}
            filterOption={userFilterOption}
            onChange={(value) => updateFilter('userIds', value || [])}
          >
            {userOptions.map((option) => (
              <Option key={option.value} value={option.value} label={option.label}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Input
            placeholder="Search messages"
            prefix={<SearchOutlined />}
            style={{ width: 280 }}
            value={filters.searchText}
            onChange={(event) => updateFilter('searchText', event.target.value)}
            onPressEnter={applyFilters}
          />
          <Select
            allowClear
            placeholder="Flag status"
            style={{ width: 160 }}
            value={filters.flagged}
            onChange={(value) => updateFilter('flagged', value)}
          >
            <Option value="flagged">Flagged</Option>
            <Option value="unflagged">Unflagged</Option>
          </Select>
          <Select
            allowClear
            placeholder="Review status"
            style={{ width: 180 }}
            value={filters.reviewStatus}
            onChange={(value) => updateFilter('reviewStatus', value)}
          >
            <Option value="unreviewed">Unreviewed</Option>
            <Option value={REVIEW_STATUS.SUCCESS}>Success</Option>
            <Option value={REVIEW_STATUS.FAILURE}>Failure</Option>
          </Select>
          <RangePicker
            value={filters.dateRange}
            onChange={(value) => updateFilter('dateRange', value)}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>
            Search
          </Button>
          <Button icon={<ReloadOutlined />} onClick={resetFilters}>
            Reset
          </Button>
          <Button onClick={() => setQueryModalVisible(true)}>Show query</Button>
          {failureExportData.length ? (
            <CSVLink
              data={failureExportData}
              headers={failureExportHeaders}
              filename="assistant-conversation-failures-current-view.csv"
            >
              <Button icon={<DownloadOutlined />}>Export failures CSV</Button>
            </CSVLink>
          ) : (
            <Button icon={<DownloadOutlined />} disabled>
              Export failures CSV
            </Button>
          )}
        </Space>

        <Table
          dataSource={conversations}
          columns={columns}
          loading={loading}
          expandable={{
            expandedRowKeys,
            onExpand: handleExpand,
            expandedRowRender: renderExpandedConversation,
          }}
          pagination={{
            showQuickJumper: false,
            showSizeChanger: true,
            current,
            total,
            pageSize,
            pageSizeOptions: ['10', '20', '50', '100'],
            position: ['topLeft', 'bottomLeft'],
          }}
          onChange={handleTableChange}
          bordered
        />
      </Space>

      <AssistantConversationReviewModal
        conversation={reviewTarget}
        visible={Boolean(reviewTarget)}
        loading={reviewSaving}
        onCancel={closeReviewModal}
        onSave={handleReviewSave}
      />

      <Modal
        title="GraphQL search query"
        visible={queryModalVisible}
        width={760}
        onCancel={() => setQueryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setQueryModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Query document</div>
        <pre style={codeBlockStyle}>{FIND_MANY_QUERY_TEXT}</pre>
        <div style={{ fontWeight: 600, margin: '12px 0 6px' }}>
          Variables sent for the last search
        </div>
        <pre style={codeBlockStyle}>
          {lastQueryVariables
            ? JSON.stringify(lastQueryVariables, null, 2)
            : 'Run a search to capture the variables.'}
        </pre>
      </Modal>
    </MainLayout>
  );
}

export default withRouter(AssistantConversations);
